import * as i0 from '@angular/core';
import { Injectable, InjectionToken, NgModule, inject } from '@angular/core';
import { Subject, of, mergeMap, ignoreElements, merge, retry } from 'rxjs';
import { filter } from 'rxjs/operators';

const CREATE_TRACKER_METADATA_KEY = '__tiime/create_tracker__';
function createTracker(tracker) {
    Object.defineProperty(tracker, CREATE_TRACKER_METADATA_KEY, {
        value: true,
    });
    return tracker;
}
function getCreateTrackerMetadata(instance) {
    const propertyNames = Object.getOwnPropertyNames(instance);
    return propertyNames.filter(propertyName => instance[propertyName]?.hasOwnProperty(CREATE_TRACKER_METADATA_KEY));
}

class Events extends Subject {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.0", ngImport: i0, type: Events, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.0", ngImport: i0, type: Events, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.0", ngImport: i0, type: Events, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
function withName(...allowedNames) {
    return filter((event) => allowedNames.some(allowedName => allowedName === event.name));
}

const TRACKING_GROUPS = new InjectionToken('TRACKING_GROUPS');
const TRACKING_GROUPS_INSTANCES = new InjectionToken('TRACKING_GROUPS_INSTANCES');

class TrackingModule {
    static forRoot(trackingGroups) {
        return {
            ngModule: TrackingModule,
            providers: [
                trackingGroups,
                {
                    provide: TRACKING_GROUPS,
                    useValue: trackingGroups,
                },
                {
                    provide: TRACKING_GROUPS_INSTANCES,
                    useFactory: getTrackingGroupsInstances,
                    deps: [TRACKING_GROUPS],
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.0", ngImport: i0, type: TrackingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.0", ngImport: i0, type: TrackingModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.0", ngImport: i0, type: TrackingModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.0", ngImport: i0, type: TrackingModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });
function getTrackingGroupsInstances(trackingGroups) {
    return trackingGroups.map((trackingGroup) => inject(trackingGroup));
}

class TrackingService {
    constructor() {
        this.trackingSub = null;
        this.trackingGroupsInstances = inject(TRACKING_GROUPS_INSTANCES);
        this.events$ = inject(Events);
    }
    ngOnDestroy() {
        this.stopTracking();
    }
    dispatch(event) {
        this.events$.next(event);
    }
    startTracking() {
        if (this.trackingSub) {
            return;
        }
        this.trackingSub = of(...this.trackingGroupsInstances)
            .pipe(mergeMap(trackingGroupInstance => {
            const trackersObservables$ = getCreateTrackerMetadata(trackingGroupInstance).map((propertyName) => {
                const trackerObservable$ = trackingGroupInstance[propertyName];
                return trackerObservable$.pipe(ignoreElements());
            });
            return merge(...trackersObservables$);
        }), retry())
            .subscribe();
    }
    stopTracking() {
        if (this.trackingSub) {
            this.trackingSub.unsubscribe();
            this.trackingSub = null;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.0", ngImport: i0, type: TrackingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.0", ngImport: i0, type: TrackingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.0", ngImport: i0, type: TrackingService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/*
 * Public API Surface of tiime-tracking
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CREATE_TRACKER_METADATA_KEY, Events, TrackingModule, TrackingService, createTracker, getCreateTrackerMetadata, getTrackingGroupsInstances, withName };
